// Offline MP4 export, built for compatibility with players and NLEs:
// - H.264 (High profile, level chosen to fit the size/rate/bitrate; Main or
//   Baseline only if the platform can't do High), 4:2:0 limited-range BT.709,
//   tagged with a `colr` box; even frame dimensions; constant frame rate with
//   exact NTSC timing (23.976/29.97/59.94 use a 1001-tick timescale).
// - AAC-LC 48 kHz stereo, interleaved with the video (not all audio up front).
// - Standard non-fragmented MP4 with the moov box first ("fast start"), written
//   progressively — to a file you choose (any size) or to a Blob for download.
// - Any encoder error aborts the export instead of producing a broken file, and
//   the finished file's box structure is verified.
// Frames are composited with the same draw code as the live monitor.
import { Muxer, StreamTarget } from "mp4-muxer";
import { Project } from "../state/model";
import { Store } from "../state/store";
import { drawClipFrame, layersAt } from "../video/compositor";
import { generatorFrame } from "../video/generators";
import { makeThumbnailVideo } from "../video/source";
import { projectAudioEnd } from "./render";
import { AacFeeder, AAC_CHANNELS, AAC_RATE } from "./export-audio";
import { BlobSink, ExportSink, checkMp4 } from "./sink";

export interface FrameRate { num: number; den: number; label: string; }
export const FRAME_RATES: FrameRate[] = [
  { num: 24000, den: 1001, label: "23.976" }, { num: 24, den: 1, label: "24" }, { num: 25, den: 1, label: "25" },
  { num: 30000, den: 1001, label: "29.97" }, { num: 30, den: 1, label: "30" }, { num: 50, den: 1, label: "50" },
  { num: 60000, den: 1001, label: "59.94" }, { num: 60, den: 1, label: "60" },
];
/** The listed rate nearest to a plain fps number (29.97 → 30000/1001). */
export function frameRateFor(fps: number): FrameRate {
  let best = FRAME_RATES[4];
  for (const r of FRAME_RATES) if (Math.abs(r.num / r.den - fps) < Math.abs(best.num / best.den - fps)) best = r;
  return best;
}

export type ExportQuality = "high" | "medium" | "small";
const BPP: Record<ExportQuality, number> = { high: 0.12, medium: 0.075, small: 0.045 }; // bits per pixel per frame

export interface VideoExportSettings {
  width: number; height: number;
  rate: FrameRate;
  quality: ExportQuality;
  start: number; end: number;
}

export interface ExportOpts {
  settings?: Partial<VideoExportSettings>;
  sink?: ExportSink;
  signal?: AbortSignal;
  onProgress?: (phase: "audio" | "video" | "finalize", frac: number) => void;
}

export function videoExportSupported(): boolean {
  return typeof VideoEncoder !== "undefined" && typeof AudioEncoder !== "undefined" && typeof VideoFrame !== "undefined";
}

/** Timeline end (latest enabled clip). */
export function contentDuration(project: Project): number { return projectAudioEnd(project); }

/** Export sizes with the project's aspect ratio (even dimensions, 4:2:0-safe). */
export function exportSizes(project: Project): { w: number; h: number; label: string }[] {
  const ar = project.width / project.height;
  const even = (n: number) => Math.max(2, Math.round(n / 2) * 2);
  const out = [{ w: even(project.width), h: even(project.height), label: `${even(project.width)}×${even(project.height)} (project)` }];
  for (const h of [2160, 1440, 1080, 720, 540, 480]) {
    const w = even(h * ar);
    if (!out.some((o) => o.w === w && o.h === h)) out.push({ w, h: even(h), label: `${w}×${even(h)}${h === 2160 ? " (4K UHD)" : h === 1080 ? " (Full HD)" : h === 720 ? " (HD)" : ""}` });
  }
  return out;
}

// H.264 levels: max macroblocks/s, max frame size (MBs), max bitrate (High, bps)
const LEVELS: [string, number, number, number][] = [
  ["1f", 108000, 3600, 17.5e6], ["20", 216000, 5120, 25e6], ["28", 245760, 8192, 25e6], ["29", 245760, 8192, 62.5e6],
  ["2a", 522240, 8704, 62.5e6], ["32", 589824, 22080, 168.75e6], ["33", 983040, 36864, 300e6], ["34", 2073600, 36864, 300e6],
];
function levelsFor(w: number, h: number, fps: number, bitrate: number): string[] {
  const fs = Math.ceil(w / 16) * Math.ceil(h / 16);
  return LEVELS.filter(([, mbps, mfs, br]) => fs <= mfs && fs * fps <= mbps && bitrate <= br).map(([hex]) => hex);
}

/** First H.264 configuration the platform can encode: High at the lowest fitting
 *  level, then Main, then Constrained Baseline. */
async function pickAvcConfig(w: number, h: number, fps: number, bitrate: number): Promise<VideoEncoderConfig> {
  const levels = levelsFor(w, h, fps, bitrate);
  if (!levels.length) throw new Error(`${w}×${h} at ${fps.toFixed(2)} fps is beyond H.264's limits — pick a smaller size or rate.`);
  const base = { width: w, height: h, bitrate, framerate: fps, bitrateMode: "variable" as const, latencyMode: "quality" as const, avc: { format: "avc" as const } };
  for (const profile of ["6400", "4d40", "42e0"]) {
    for (const lv of levels.slice(0, 3)) {
      const cfg: VideoEncoderConfig = { ...base, codec: `avc1.${profile}${lv}` };
      try { if ((await VideoEncoder.isConfigSupported(cfg)).supported) return cfg; } catch { /* next */ }
    }
  }
  throw new Error("This browser can't encode H.264 at this size. Try a smaller export size, or Chrome/Edge on desktop.");
}

/** Seek an element and wait until the target frame can be drawn (never hangs). */
function seekFrame(el: HTMLVideoElement, t: number): Promise<void> {
  return new Promise<void>((res) => {
    const ready = () => el.readyState >= 2 && !el.seeking;
    if (Math.abs(el.currentTime - t) < 1e-4 && ready()) { res(); return; }
    let done = false;
    const finish = () => { if (done) return; done = true; clearTimeout(to); el.removeEventListener("seeked", onSeeked); res(); };
    const onSeeked = () => {
      if (ready()) { finish(); return; }
      el.addEventListener("loadeddata", finish, { once: true }); el.addEventListener("canplay", finish, { once: true });
    };
    const to = setTimeout(finish, 5000); // a stuck decoder must not hang the export
    el.addEventListener("seeked", onSeeked);
    try { el.currentTime = Math.max(0, t); } catch { finish(); }
  });
}

/** Composite + encode the timeline to MP4. Resolves to the Blob (download sink)
 *  or null (file sink — the file is already on disk). */
export async function exportVideo(store: Store, opts: ExportOpts = {}): Promise<Blob | null> {
  if (!videoExportSupported()) throw new Error("Video export needs WebCodecs (use Chrome or Edge).");
  const project = store.project;
  const s: VideoExportSettings = {
    width: project.width, height: project.height, rate: frameRateFor(project.fps || 30), quality: "high",
    start: 0, end: contentDuration(project), ...opts.settings,
  };
  const W = Math.max(2, Math.round(s.width / 2) * 2), H = Math.max(2, Math.round(s.height / 2) * 2);
  const fps = s.rate.num / s.rate.den;
  const dur = s.end - s.start;
  if (!(dur > 0)) throw new Error("Nothing to export — add a clip to the timeline first.");
  if (!(await AacFeeder.supported())) throw new Error("This browser can't encode AAC audio — use Chrome or Edge.");

  const frameCount = Math.max(1, Math.round(dur * fps));
  const bitrate = Math.round(Math.max(1.5e6, W * H * fps * BPP[s.quality]));
  const cfg = await pickAvcConfig(W, H, fps, bitrate);
  const sink = opts.sink ?? new BlobSink("video/mp4");
  const abortCheck = () => { if (opts.signal?.aborted) throw new DOMException("Export cancelled", "AbortError"); };

  let encError: unknown = null;
  const muxer = new Muxer({
    target: new StreamTarget({ onData: (d, p) => sink.write(d, p), chunked: true, chunkSize: 8 * 1024 * 1024 }),
    // Integer frame rates use timescale = fps; NTSC rates use num (e.g. 30000) with 1001-tick frames.
    video: { codec: "avc", width: W, height: H, frameRate: s.rate.num },
    audio: { codec: "aac", numberOfChannels: AAC_CHANNELS, sampleRate: AAC_RATE },
    // moov reserved at the front, so the file is "fast start" yet written progressively
    fastStart: { expectedVideoChunks: frameCount, expectedAudioChunks: Math.ceil((dur * AAC_RATE) / 1024) + 64 },
    firstTimestampBehavior: "offset",
  });

  const videoEncoder = new VideoEncoder({
    output: (chunk, meta) => {
      // Tag BT.709 explicitly when the platform encoder doesn't report a color space,
      // so NLEs don't have to guess (they'd often assume BT.601 → shifted colors).
      if (meta?.decoderConfig && !meta.decoderConfig.colorSpace) {
        meta = { ...meta, decoderConfig: { ...meta.decoderConfig, colorSpace: { primaries: "bt709", transfer: "bt709", matrix: "bt709", fullRange: false } } };
      }
      try { muxer.addVideoChunk(chunk, meta); } catch (e) { encError ??= e; }
    },
    error: (e) => { encError ??= e; },
  });
  videoEncoder.configure(cfg);
  const audio = new AacFeeder(project, store.buffers, { start: s.start, end: s.end }, {}, (c, m) => { try { muxer.addAudioChunk(c, m); } catch (e) { encError ??= e; } });

  // Dedicated seek elements per (track, asset, layer) — not the live monitor's —
  // so the same media on two tracks, or on both sides of a dissolve, can show two
  // different times. Created on first use.
  const els = new Map<string, HTMLVideoElement | null>();
  const elFor = async (key: string, assetId: string) => {
    if (els.has(key)) return els.get(key)!;
    const src = store.videos.get(assetId);
    if (!src) { els.set(key, null); return null; }
    const el = makeThumbnailVideo(src.url);
    await new Promise<void>((res) => { if (el.readyState >= 1) res(); else { el.addEventListener("loadedmetadata", () => res(), { once: true }); el.addEventListener("error", () => res(), { once: true }); } });
    els.set(key, el);
    return el;
  };
  const mediaDur = (c: { assetId: string }) => store.asset(c.assetId)?.duration || Infinity;
  const canvas = new OffscreenCanvas(W, H);
  const g = canvas.getContext("2d", { alpha: false }) as OffscreenCanvasRenderingContext2D;
  const tracks = project.tracks;
  const gop = Math.max(1, Math.round(fps * 2)); // keyframe every ~2 s (seek-friendly in NLEs)
  const usAt = (i: number) => Math.round((i * s.rate.den * 1e6) / s.rate.num);

  try {
    for (let i = 0; i < frameCount; i++) {
      abortCheck();
      if (encError) throw encError;
      const t = (i * s.rate.den) / s.rate.num; // seconds into the export
      // keep audio ~half a second ahead of video so the file interleaves tightly
      await audio.feedUntil(t + 0.5);
      const pos = s.start + t;
      g.fillStyle = "#000";
      g.fillRect(0, 0, W, H);
      // top-down: later tracks paint first (bottom), earlier tracks on top
      for (let ti = tracks.length - 1; ti >= 0; ti--) {
        const track = tracks[ti];
        if (track.kind !== "video" || track.disabled) continue;
        for (const { clip, alpha, time, layer } of layersAt(track.clips, pos, mediaDur)) {
          const gen = store.asset(clip.assetId)?.generator;
          if (gen) { // generated media: drawn from its spec at the export resolution
            drawClipFrame(g, generatorFrame(`export|${clip.id}|${layer}`, gen, W, H, time, fps), W, H, W, H, clip, alpha);
            continue;
          }
          const el = await elFor(`${track.id}|${clip.assetId}|${layer}`, clip.assetId);
          if (!el) continue;
          await seekFrame(el, time + 0.001); // a hair past the frame start → never the previous frame
          if (el.readyState >= 2) drawClipFrame(g, el, el.videoWidth, el.videoHeight, W, H, clip, alpha);
        }
      }
      const ts = usAt(i);
      const frame = new VideoFrame(canvas, { timestamp: ts, duration: usAt(i + 1) - ts });
      videoEncoder.encode(frame, { keyFrame: i % gop === 0 });
      frame.close();
      while (videoEncoder.encodeQueueSize > 6) await new Promise((r) => setTimeout(r, 2)); // backpressure
      if (i % 30 === 0) await sink.drain();
      opts.onProgress?.("video", (i + 1) / frameCount);
    }
    await videoEncoder.flush();
    await audio.finish();
    if (encError) throw encError;
    opts.onProgress?.("finalize", 1);
    muxer.finalize();
    const blob = await sink.close();
    const written = blob ?? (await sink.result());
    if (written) {
      const problem = await checkMp4(written);
      if (problem) throw new Error(`The exported file failed verification (${problem}).`);
    }
    return blob;
  } catch (e) {
    await sink.abort();
    throw e;
  } finally {
    try { videoEncoder.close(); } catch {}
    audio.close();
    for (const el of els.values()) { if (!el) continue; el.removeAttribute("src"); try { el.load(); } catch {} }
  }
}
